import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  arePrimitiveObjectsEqual,
  editCardValidator,
  getDefaultFormValuesFromRestriction,
} from "shared";
import { toast } from "sonner";
import type { z } from "zod";
import { AdvancedFields } from "@/components/fields/advanced-fields";
import {
  RestrictionsInputField,
  restrictionsInputFieldMapping,
} from "@/components/fields/restrictions-input-field";
import { FormStepSeparator } from "@/components/ui/form-step-seperator";
import { useAppForm } from "@/hooks/useCreateForm";
import { useRequiredRole } from "@/hooks/useRequiredRole";

export const Route = createFileRoute("/_authed/_org/cards/$id/edit")({
  component: RouteComponent,
});

function RouteComponent() {
  const { id: cardId } = Route.useParams();
  const { trpc } = Route.useRouteContext();
  const { admin } = useRequiredRole();

  const { data: card } = useSuspenseQuery(
    trpc.card.get.queryOptions({
      cardId,
    }),
  );
  const { restriction } = card;

  const { mutate: editCard, isPending } = useMutation(
    trpc.card.edit.mutationOptions({
      onSuccess: async (_data) => {
        form.reset();
        toast.success("Updated Card", {
          position: "bottom-left",
        });
      },
    }),
  );

  const { activateAt, lockAt, ...editableRestrictionsDefaults } =
    getDefaultFormValuesFromRestriction(restriction);

  const defaultValues = {
    approvalPolicyId: card.approvalPolicyId,
    defaultBudgetId: card.defaultBudgetId,
    description: card.description as string | null,
    name: card.name,
    policyId: card.policyId,
    restrictions: {
      ...editableRestrictionsDefaults,
      schedule: { activateAt, lockAt },
    },
  } as z.infer<typeof editCardValidator>;

  const form = useAppForm({
    defaultValues,
    onSubmit: ({ value }) => {
      const { restrictions: _restrictions, ...rest } = value;
      const dataToSubmit = {
        ...rest,
        cardId,
        restrictions: arePrimitiveObjectsEqual(
          defaultValues.restrictions!,
          value.restrictions ?? {},
        )
          ? null
          : value.restrictions,
      };

      editCard(dataToSubmit);
    },
    validators: {
      onSubmit: editCardValidator,
    },
  });

  const { data: approvalPolicies } = useSuspenseQuery(
    trpc.approvalPolicy.list.queryOptions(),
  );

  return (
    <form.AppForm>
      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-2 pb-4">
          <FormStepSeparator description="Details" />
          <div className="flex flex-col space-y-4 pb-2">
            <form.AppField
              children={(field) => {
                return (
                  <field.TextField label="Card Nickname" required={true} />
                );
              }}
              name="name"
            />

            <form.AppField
              children={(field) => {
                return (
                  <field.TextAreaField label="Description" required={false} />
                );
              }}
              name="description"
            />
          </div>

          {admin && (
            <>
              <FormStepSeparator description="Policies" />

              <div className="flex flex-col gap-4 pb-2">
                <form.AppField
                  children={(field) => (
                    <field.ExpensePolicySelectField label="Spend Policy" />
                  )}
                  name="policyId"
                />

                {approvalPolicies.length > 0 && (
                  <form.AppField
                    children={(field) => (
                      <field.ApprovalPolicySelectField label="Approval Policy" />
                    )}
                    name="approvalPolicyId"
                  />
                )}
              </div>

              <FormStepSeparator description="Control Spend" />

              <div className="flex flex-col gap-4 pb-2">
                <RestrictionsInputField
                  form={form}
                  fields={restrictionsInputFieldMapping}
                />
              </div>

              <div className="mt-6 mb-12 h-px w-full bg-border" />

              <AdvancedFields>
                <form.AppField
                  name="defaultBudgetId"
                  children={(field) => (
                    <field.BudgetSearchSelectField
                      label="Default Budget"
                      descriptionStyle="below"
                      description="Transactions on this card will be assigned to this budget by default"
                      buttonClassName="[&_*[data-placeholder=true]]:text-muted-foreground font-normal"
                      regressionToNullOption
                      required={false}
                    />
                  )}
                />
              </AdvancedFields>
            </>
          )}
        </div>

        <div className="flex min-h-16 flex-row items-center justify-around border-t p-6">
          <form.ConditionalSubmitCancelButtons isPending={isPending} />
        </div>
      </form>
    </form.AppForm>
  );
}
